import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

console.log("[Auth] API_URL:", API_URL, "| window.origin:", window.location.origin);

// Token işlemleri için yardımcı fonksiyonlar
const saveTokens = (accessToken, refreshToken) => {
  // Access token'ı localStorage'a kaydet
  localStorage.setItem("rovionToken", accessToken);
  localStorage.setItem("rovionRefreshToken", refreshToken);
};

const getTokens = () => {
  // localStorage'dan token al
  const accessToken = localStorage.getItem("rovionToken");
  const refreshToken = localStorage.getItem("rovionRefreshToken");
  return { accessToken, refreshToken };
};

const removeTokens = () => {
  // localStorage'dan token'ları sil
  localStorage.removeItem("rovionToken");
  localStorage.removeItem("rovionRefreshToken");
};

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastFetchRef = useRef(0);
  const fetchTimeoutRef = useRef(null);
  const MIN_FETCH_INTERVAL = 30000; // 30 saniye
  const refreshTimeoutRef = useRef(null);

  // Token yenileme işlemi
  const refreshTokens = async () => {
    const { refreshToken } = getTokens();
    try {
      // Call backend refresh endpoint
      const response = await axios.post(`${API_URL}/api/auth/refresh`, {
        refreshToken: refreshToken,
      });

      if (response.data && response.data.success) {
        saveTokens(response.data.token, response.data.refreshToken);
        return response.data.token;
      } else {
        throw new Error("Failed to refresh token");
      }
    } catch (error) {
      removeTokens();
      setUser(null);
      throw error;
    }
  };

  // Axios interceptor'ı ekle
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't intercept login or refresh endpoint errors
        const isLoginEndpoint =
          originalRequest?.url?.includes("/api/auth/login");
        const isRefreshEndpoint =
          originalRequest?.url?.includes("/api/auth/refresh");

        // Token hatası ve henüz yeniden denenmemiş ise
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !isLoginEndpoint &&
          !isRefreshEndpoint
        ) {
          originalRequest._retry = true;

          try {
            const newToken = await refreshTokens();
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            // Token yenileme başarısız, kullanıcıyı login sayfasına yönlendir
            removeTokens();
            setUser(null);
            window.location.href = "/login";
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );

    // Cleanup
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Debounce ile API çağrısını optimize et
  const getUserFromToken = useCallback(
    async (force = false) => {
      const now = Date.now();

      if (!force && now - lastFetchRef.current < MIN_FETCH_INTERVAL) {
        return user;
      }

      try {
        const { accessToken } = getTokens();
        if (!accessToken) {
          setUser(null);
          return null;
        }

        lastFetchRef.current = now;

        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });

          if (response.data && response.data.success && response.data.user) {
            setUser(response.data.user);
            return response.data.user;
          } else {
            removeTokens();
            setUser(null);
            return null;
          }
        } catch (apiError) {
          if (apiError.response?.status === 401) {
            try {
              await refreshTokens();
              // Token yenilendikten sonra tekrar dene
              return getUserFromToken(true);
            } catch (refreshError) {
              removeTokens();
              setUser(null);
              return null;
            }
          }
          removeTokens();
          setUser(null);
          return null;
        }
      } catch (error) {
        removeTokens();
        setUser(null);
        return null;
      }
    },
    [user],
  );

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await getUserFromToken(true);
      } catch (error) {
        removeTokens();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
      });

      if (response.data && response.data.token) {
        saveTokens(response.data.token, response.data.refreshToken);

        if (response.data.user) {
          setUser(response.data.user);
        }

        return { success: true };
      } else {
        return { success: false, message: "Giriş başarısız: Token alınamadı" };
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Giriş işlemi sırasında bir hata oluştu";
      return {
        success: false,
        message: errorMessage,
      };
    }
  };

  const logout = () => {
    try {
      // Call backend logout
      axios.post(`${API_URL}/api/auth/logout`, {}).catch(() => {}); // Ignore errors for logout

      removeTokens();
      setUser(null);
      return true;
    } catch (err) {
      return false;
    }
  };

  const getToken = useCallback(() => {
    const { accessToken } = getTokens();
    return accessToken;
  }, []);

  const getUser = useCallback(() => {
    return user;
  }, [user]);

  const hasPageWriteAccess = useCallback(
    (path) => {
      if (user?.fullAccess || user?.role === "admin") return true;
      const pages = user?.pages || [];
      return pages.includes(`${path}:write`);
    },
    [user],
  );

  return {
    user,
    loading,
    getUser,
    getToken,
    getUserFromToken,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    role: user?.role || "dealer",
    hasPageWriteAccess,
  };
};

export default useAuth;
